import { ApiProblem, Credentials, Deadline } from '@cdn/api';
import type { Identity, Method, SignInRequest } from '../port';

interface Specs {
	/** Origin of the CDN service, which holds the verifier and the session cookie */
	service: string;
	/** Where the person lands after signing in: a path of this application */
	returning: string;
	fetch?: typeof fetch;
	/** Sends the browser away; tests observe it instead of leaving */
	navigate?: (url: string) => void;
	/** Milliseconds a request to the service may take (`Deadline.DEFAULT` otherwise) */
	timeout?: number;
}

/**
 * Signing in through Beyond Accounts, the suite's shared human identity.
 *
 * This application is a static page: it has no server, so it cannot hold the verifier a hand-off is
 * redeemed with, and it must not — a verifier published to a browser protects nothing. The CDN
 * service does both, and what the browser ends up with is an `HttpOnly` cookie of the service's own
 * origin that no script here can read.
 *
 * That is why this adapter never returns a real credential. It answers the sentinel the API client
 * understands as "send the cookie", so the credential is never in `sessionStorage`, never in a
 * variable and never in a stack trace — which matters in a console whose screens render metadata
 * that came from somewhere else.
 *
 * Organizations, roles and capabilities still come only from the CDN session document, exactly as
 * with every other adapter. Nothing about who someone is arrives from here.
 */
export class PlatformIdentity implements Identity {
	readonly name = 'platform' as const;
	/** Exercised against a real service and a real authority in `test/foundation/accounts-handoff.test.mjs` */
	readonly validated = true;
	readonly methods: readonly Method[] = ['platform'];

	#service: string;
	#returning: string;
	#fetch: typeof fetch;
	#navigate: (url: string) => void;
	#timeout: number;

	#ended: string | undefined;
	/** Why the service's session for this browser ended, when the authority said so (D26) */
	get ended() {
		return this.#ended;
	}

	constructor({ service, returning, fetch: fetcher, navigate, timeout }: Specs) {
		this.#service = service.replace(/\/+$/, '');
		this.#returning = returning;
		this.#fetch = fetcher ?? ((...args) => globalThis.fetch(...args));
		this.#navigate = navigate ?? (url => globalThis.location.assign(url));
		this.#timeout = timeout ?? Deadline.DEFAULT;
	}

	/**
	 * Whether the service still holds a session for this browser.
	 *
	 * The cookie cannot be read here, so the service is asked. Answering from the cookie's presence
	 * alone would report a session that the authority had already ended. Only `{signed: false}` (or a 401)
	 * is signed out: an authority that did not answer (`UPSTREAM_UNAVAILABLE`) and a service without a
	 * configured sign-in (`NOT_IMPLEMENTED`) are thrown, and the session shows an outage instead. So is an
	 * answer that did not arrive within the deadline.
	 */
	async restore(): Promise<string | undefined> {
		const read = async (signal: AbortSignal) => {
			const response = await this.#fetch(`${this.#service}/auth/session`, { credentials: 'include', headers: { accept: 'application/json' }, signal });
			if (response.status === 401 || response.status === 403) return undefined;
			if (!response.ok) throw ApiProblem.from(response.status, await response.json().catch(() => undefined));
			const document = (await response.json().catch(() => undefined)) as { signed?: boolean; reason?: unknown } | undefined;
			this.#ended = !document?.signed && typeof document?.reason === 'string' ? document.reason : undefined;
			return document?.signed ? Credentials.COOKIE : undefined;
		};
		try {
			return await new Deadline(this.#timeout).run(read);
		} catch (thrown) {
			if (thrown instanceof ApiProblem) throw thrown;
			// No answer at all is an outage, never a missing session: the person would be sent to sign in for nothing
			throw new ApiProblem('UNAVAILABLE', 'The service did not answer', 0);
		}
	}

	/**
	 * Leaves for the shared authority. The browser comes back to the return route, so nothing is
	 * returned here: the port's contract for an adapter that navigates away.
	 */
	async signin(request: SignInRequest): Promise<string | undefined> {
		if (request.method !== 'platform') throw new Error(`This installation signs in through Beyond Accounts, not "${request.method}"`);
		const start = new URL(`${this.#service}/auth/start`);
		start.searchParams.set('to', this.#returning);
		this.#navigate(start.toString());
		return undefined;
	}

	/**
	 * The service's hand-off start for a renewal of the page's `Session`: `silent` asks Accounts `prompt=none` in a
	 * hidden frame, `window` signs in in a window of its own, and both end on this application's session landing;
	 * `tab` returns to `here`, the address in view
	 */
	start(mode: 'silent' | 'window' | 'tab', here: string): string {
		const start = new URL(`${this.#service}/auth/start`);
		start.searchParams.set('mode', mode);
		start.searchParams.set('to', here);
		return start.toString();
	}

	/**
	 * Ends the session at the authority that issued it. Dropping it here would not be signing out:
	 * the cookie would still authenticate on the next request.
	 */
	async signout(): Promise<void> {
		const send = (signal: AbortSignal) => this.#fetch(`${this.#service}/auth/signout`, { method: 'POST', credentials: 'include', headers: { accept: 'application/json' }, signal });
		await new Deadline(this.#timeout).run(send).catch(() => undefined);
	}
}
